import React, { useEffect, useState } from 'react';

interface ProgressIndicatorProps {
  position?: 'top' | 'bottom' | 'left' | 'right';
  thickness?: number;
  color?: string;
  showPercentage?: boolean;
  className?: string;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  position = 'top',
  thickness = 3,
  color = 'var(--orange-primary)',
  showPercentage = false,
  className = ''
}) => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const calculateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    calculateScrollProgress();
    window.addEventListener('scroll', calculateScrollProgress, { passive: true });

    return () => {
      window.removeEventListener('scroll', calculateScrollProgress);
    };
  }, []);

  const getPositionClasses = () => {
    switch (position) {
      case 'top':
        return 'top-0 left-0 w-full';
      case 'bottom':
        return 'bottom-0 left-0 w-full';
      case 'left':
        return 'top-0 left-0 h-full';
      case 'right':
        return 'top-0 right-0 h-full';
      default:
        return 'top-0 left-0 w-full';
    }
  };

  const getProgressStyle = () => {
    const isHorizontal = position === 'top' || position === 'bottom';
    
    return {
      height: isHorizontal ? `${thickness}px` : '100%',
      width: isHorizontal ? '100%' : `${thickness}px`,
      background: `linear-gradient(${isHorizontal ? '90deg' : '0deg'}, ${color}, ${color}80)`,
      transform: isHorizontal 
        ? `scaleX(${scrollProgress / 100})` 
        : `scaleY(${scrollProgress / 100})`,
      transformOrigin: isHorizontal ? 'left center' : 'top center',
      transition: 'transform 0.1s ease-out',
      boxShadow: `0 0 ${thickness * 2}px ${color}40`
    };
  };

  return (
    <>
      {/* Progress Bar */}
      <div 
        className={`fixed z-50 ${getPositionClasses()} ${className}`}
        style={{
          background: 'rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <div style={getProgressStyle()} />
      </div>

      {/* Percentage Indicator */}
      {showPercentage && (
        <div 
          className="fixed top-4 right-4 z-50 px-3 py-1 rounded-full text-xs font-medium"
          style={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            color: color,
            border: `1px solid ${color}30`,
            opacity: scrollProgress > 5 ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        >
          {Math.round(scrollProgress)}%
        </div>
      )}
    </>
  );
};

// Reading progress indicator with estimated time
export const ReadingProgressIndicator: React.FC = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(0);

  useEffect(() => {
    const calculateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      
      setScrollProgress(Math.min(progress, 100));
      
      // Estimate reading time based on content length (rough calculation)
      const remainingHeight = docHeight - scrollTop;
      const avgScrollSpeed = 1000; // pixels per minute (rough estimate)
      const timeRemaining = Math.ceil(remainingHeight / avgScrollSpeed);
      setEstimatedTime(Math.max(timeRemaining, 0));
    };

    calculateProgress();
    window.addEventListener('scroll', calculateProgress, { passive: true });

    return () => {
      window.removeEventListener('scroll', calculateProgress);
    };
  }, []);

  return null;
};